/**
 * WEB-447 A3: AI-selected font support for the public funnel render.
 *
 * settings.fontId (read-only from the FE's perspective — the backend owns the
 * schema) selects a heading/body font pair from a frozen, small catalogue.
 * This module resolves that id to ready-to-use CSS font-family strings.
 *
 * WEB-2015 (17.09.2026, GDPR «всё своё»): every family every fontId can
 * resolve to is self-hosted (`../styles/selfHostedFonts.ts`) — nothing here
 * requests Google Fonts any more; see `ensureFontsLoaded` /
 * `ensureThemeFontsLoaded` below.
 */

/**
 * WEB-672 atom A: catalogue extended 7 → 15 (PM decision 2026-07-16, WW
 * segment). Keep in sync with backend FUNNEL_FONT_IDS
 * (backend/src/ai-funnels/block-schema/funnel-settings.ts). Additive only —
 * legacy ids are never removed so persisted funnels keep rendering.
 */
import { customFontFamilyOf } from './customFontRef';

export const FONT_IDS = [
  // sans
  'inter',
  'roboto',
  'opensans',
  'montserrat',
  'poppins',
  'lato',
  'manrope',
  // serif
  'playfair',
  'merriweather',
  'lora',
  'ptserif',
  'dmserif',
  // accent
  'oswald',
  'raleway',
  'philosopher',
  // system — решение владельца №1 (2026-10-08, повтор Slimkit): системный
  // стек устройства, НИЧЕГО не скачивается (правило самохостинга WEB-2015 не
  // задето). Последним: порядок = порядок групп в BlockFontSelect.
  'system',
] as const;

export type FontId = (typeof FONT_IDS)[number];

/**
 * WEB-672 atom A — no-tofu fallback. The public player renders translated
 * copy (Arabic/CJK/Devanagari/…) while most curated families only cover
 * Latin/Cyrillic; without a pan-script webfont in the stack, missing glyphs
 * render as tofu boxes on devices whose system fonts also lack the script.
 * Every stack therefore falls back to Noto (Sans for sans/accent stacks,
 * Serif for serif headings) before the generic family. Noto Sans/Serif are
 * self-hosted (WEB-2015) alongside every other family in the pair — the
 * matching `@font-face` unicode-range subsets in `global.css` mean browsers
 * only download the subsets a page actually uses (no extra per-script Noto
 * files needed).
 */
const SANS_FALLBACK = ', "Noto Sans", sans-serif';
const SERIF_FALLBACK = ', "Noto Serif", serif';

interface FontDefinition {
  headingFamily: string;
  headingIsSerif: boolean;
  bodyFamily: string;
  googleFamilies: readonly string[];
  /**
   * Готовый стек вместо одного семейства в кавычках — только у `system`:
   * `-apple-system`/`BlinkMacSystemFont` — ключевые слова, а не имена, в
   * кавычках они перестают работать. Отдаётся и заголовку, и тексту.
   */
  stack?: string;
}

/**
 * Системный стек эталона Slimkit (их `index.html`): на iPhone это SF Pro, на
 * Windows — Segoe UI, на Android — Roboto. Noto Sans перед `sans-serif` — то
 * же правило «без тофу» (WEB-672), что у всего каталога; латиницу и кириллицу
 * закрывают системные семейства раньше, так что Noto не скачивается.
 * ⚠ `Roboto` у нас объявлен `@font-face` (самораздача, global.css) — где
 * системных SF/Segoe нет (Linux, часть Android), браузер возьмёт НАШ файл
 * Roboto, а не системный: вид тот же, запрос — к своему домену.
 */
export const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Noto Sans", sans-serif';

const FONT_MAP: Record<FontId, FontDefinition> = {
  inter: {
    headingFamily: 'Inter',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Inter'],
  },
  // WEB-672 A: roboto/opensans/lato are workhorse text faces — self-paired
  // (heading = body) like inter, so picking them changes the body texture.
  // Display-leaning additions (montserrat/oswald/raleway) and serif headings
  // (merriweather/ptserif) pair with the neutral Inter body, matching the
  // legacy poppins/manrope/playfair pattern.
  roboto: {
    headingFamily: 'Roboto',
    headingIsSerif: false,
    bodyFamily: 'Roboto',
    googleFamilies: ['Roboto'],
  },
  opensans: {
    headingFamily: 'Open Sans',
    headingIsSerif: false,
    bodyFamily: 'Open Sans',
    googleFamilies: ['Open Sans'],
  },
  montserrat: {
    headingFamily: 'Montserrat',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Montserrat', 'Inter'],
  },
  lato: {
    headingFamily: 'Lato',
    headingIsSerif: false,
    bodyFamily: 'Lato',
    googleFamilies: ['Lato'],
  },
  merriweather: {
    headingFamily: 'Merriweather',
    headingIsSerif: true,
    bodyFamily: 'Inter',
    googleFamilies: ['Merriweather', 'Inter'],
  },
  ptserif: {
    headingFamily: 'PT Serif',
    headingIsSerif: true,
    bodyFamily: 'Inter',
    googleFamilies: ['PT Serif', 'Inter'],
  },
  oswald: {
    headingFamily: 'Oswald',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Oswald', 'Inter'],
  },
  raleway: {
    headingFamily: 'Raleway',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Raleway', 'Inter'],
  },
  poppins: {
    headingFamily: 'Poppins',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Poppins', 'Inter'],
  },
  manrope: {
    headingFamily: 'Manrope',
    headingIsSerif: false,
    bodyFamily: 'Inter',
    googleFamilies: ['Manrope', 'Inter'],
  },
  playfair: {
    headingFamily: 'Playfair Display',
    headingIsSerif: true,
    bodyFamily: 'Inter',
    googleFamilies: ['Playfair Display', 'Inter'],
  },
  lora: {
    headingFamily: 'Lora',
    headingIsSerif: true,
    bodyFamily: 'Inter',
    googleFamilies: ['Lora', 'Inter'],
  },
  philosopher: {
    headingFamily: 'Philosopher',
    headingIsSerif: true,
    bodyFamily: 'Open Sans',
    googleFamilies: ['Philosopher', 'Open Sans'],
  },
  dmserif: {
    headingFamily: 'DM Serif Display',
    headingIsSerif: true,
    bodyFamily: 'Inter',
    googleFamilies: ['DM Serif Display', 'Inter'],
  },
  system: {
    headingFamily: 'system-ui',
    headingIsSerif: false,
    bodyFamily: 'system-ui',
    googleFamilies: [],
    stack: SYSTEM_FONT_STACK,
  },
};

const DEFAULT_FONT_ID: FontId = 'inter';

function isFontId(value: string | undefined): value is FontId {
  return value !== undefined && (FONT_IDS as readonly string[]).includes(value);
}

/** Validate an untrusted fontId and fall back to the contract default (inter). */
function resolveFontId(fontId: string | undefined): FontId {
  return isFontId(fontId) ? fontId : DEFAULT_FONT_ID;
}

function quoteFamily(name: string): string {
  return `"${name}"`;
}

/**
 * Семейства закрытого каталога (заголовочные и текстовые, с системным
 * `system-ui` — как у сервера, WEB-4071) — заняты для своих шрифтов воронки
 * (`reservedCustomFontFamilies`).
 */
export function catalogueFontFamilies(): string[] {
  const names = new Set<string>();
  for (const def of Object.values(FONT_MAP)) {
    names.add(def.headingFamily);
    names.add(def.bodyFamily);
  }
  return [...names];
}

export interface ResolvedFontPair {
  headingFamily: string;
  bodyFamily: string;
}

/**
 * Resolve fontId to the full CSS font-family strings, ready to assign
 * directly to a fontFamily style property. Missing/invalid ids fall back to
 * the inter mapping (contract default).
 */
export function resolveFontPair(fontId: string | undefined): ResolvedFontPair {
  // Свой шрифт воронки (`custom:<семейство>`, слово владельца 20.10.2026):
  // семейство от автора первым, дальше Inter и запасной Noto — пока файл не
  // докачался или в нём нет нужных знаков.
  const custom = customFontFamilyOf(fontId);
  if (custom) {
    const family = `${quoteFamily(custom)}, ${quoteFamily('Inter')}${SANS_FALLBACK}`;
    return { headingFamily: family, bodyFamily: family };
  }
  const def = FONT_MAP[resolveFontId(fontId)];
  if (def.stack) return { headingFamily: def.stack, bodyFamily: def.stack };
  const headingFallback = def.headingIsSerif ? SERIF_FALLBACK : SANS_FALLBACK;
  return {
    headingFamily: `${quoteFamily(def.headingFamily)}${headingFallback}`,
    bodyFamily: `${quoteFamily(def.bodyFamily)}${SANS_FALLBACK}`,
  };
}

/**
 * WEB-2015 — решение владельца 17.09.2026 («вариант 1 — всё своё», GDPR): у
 * Google Fonts не запрашивается больше НИЧЕГО. Раньше здесь стояла
 * `googleFontsHref` (строила адрес `fonts.googleapis.com/css2?...`) и
 * `ensureFontsLoaded` вставляла на неё `<link>`, когда пара шрифта была
 * самораздана не целиком. Теперь КАЖДОЕ семейство каждой пары закрытого
 * каталога (+ Noto-фолбэк) есть в реестре самораздачи
 * (`selfHostedFonts.ts`, `SELF_HOSTED_FONT_FAMILIES`) — соответствующий
 * `@font-face` уже объявлен в `global.css`, который подключён на каждой
 * странице публичного контура независимо от fontId. Отдельно грузить
 * нечего: функция — безопасный no-op, оставленный только ради вызывающих
 * (`PublicQuiz.tsx`, `PublicPaywall.tsx`, `PhonePreview.tsx`,
 * `BlockFontSelect.tsx`), у которых сигнатура не менялась.
 *
 * Гарантия «для КАЖДОЙ пары всё самораздано» — не честное слово: её держит
 * `theme-font-stylesheet-ends.web1990.spec.ts` (сверяет `googleFamilies`
 * каждого id `FONT_MAP` с реестром самораздачи ОБХОДОМ, а не списком).
 * Появится 16-й fontId с семейством без файла на диске — сторож покраснеет
 * раньше, чем эта функция молча продолжит ничего не грузить.
 */
export function ensureFontsLoaded(_fontId: string | undefined): void {
  // Намеренно пусто. `fontId` остаётся параметром ради сигнатуры вызывающих
  // (см. докблок выше). Все требуемые семейства уже покрыты `@font-face` в
  // `global.css` (реестр `selfHostedFonts.ts`) — грузить с Google нечего и
  // незачем; см. докблок выше про сторож, который поймает будущий разъезд.
}

const GENERIC_FAMILY_KEYWORDS = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  '-apple-system',
  'blinkmacsystemfont',
]);

/**
 * WEB-447 wave 1 slice 1: extract the primary (first, most-specific) font
 * family name from a CSS font-family value such as
 * `"Cormorant Garamond", "EB Garamond", Georgia, serif`. Theme presets
 * declare full fallback chains — only the first family is the intentional
 * webfont; the rest are local/system fallbacks. Returns undefined for
 * empty/generic-only input so callers can treat it as "nothing to load".
 */
export function extractPrimaryFamily(
  fontFamilyCss: string | undefined,
): string | undefined {
  if (!fontFamilyCss) return undefined;
  const first = fontFamilyCss
    .split(',')[0]
    ?.trim()
    .replace(/^["']|["']$/g, '')
    .trim();
  if (!first) return undefined;
  if (GENERIC_FAMILY_KEYWORDS.has(first.toLowerCase())) return undefined;
  return first;
}

/**
 * WEB-447 wave 1 slice 1: shape of the two theme typography fields that carry
 * font families. Deliberately structural (not imported from theme.types) so
 * this module has no dependency on the Theme type — callers pass
 * `theme.typography` directly and it matches.
 */
export interface ThemeFontFamilies {
  fontFamily?: string;
  headingFontFamily?: string;
}

/**
 * WEB-2015 — решение владельца 17.09.2026 («вариант 1 — всё своё», GDPR): у
 * Google Fonts не запрашивается больше НИЧЕГО. Раньше здесь стояла
 * `googleFontsHrefForFamilies` и эта функция вставляла `<link>` на неё для
 * пресетных семейств (`themes/presets.ts`) вне закрытого каталога. Теперь
 * каждое семейство, реально используемое пресетами, есть в реестре
 * самораздачи (`selfHostedFonts.ts`) — `@font-face` для него уже в
 * `global.css`. Отдельно грузить нечего: функция — безопасный no-op,
 * оставленный ради сигнатуры вызывающих (`PublicQuiz.tsx`,
 * `PublicPaywall.tsx`, `PhonePreview.tsx`).
 *
 * Гарантия «каждое пресетное семейство самораздано» держится
 * `presetFontsSelfHosted.web2015.test.ts` (обходом текста `presets.ts`, не
 * списком руками) — семейство без файла на диске красит сборку, а не падает
 * молча сюда.
 */
export function ensureThemeFontsLoaded(
  _typography: ThemeFontFamilies | undefined,
): void {
  // Намеренно пусто — см. докблок выше. `typography` остаётся параметром
  // ради сигнатуры вызывающих. `extractPrimaryFamily` остаётся экспортирован
  // для `BlockFontSelect.tsx` и для сторожа `presetFontsSelfHosted`.
}
